import { Fragment, type ReactNode } from 'react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { below } from '@voll-intelligence/ui-kit/breakpoints';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import { fieldPillClass } from '../field_trigger.js';

/**
 * O resumo da busca no topo das telas de resultado: a página de voo, hotel,
 * carro e ônibus do web-chat (o slot central da barra do shell) e os takeovers
 * "Ver todos" da conversa (a barra do próprio takeover, no genui-kit). Uma
 * fonte só para os dois lados: quem monta sabe o pedido e o idioma e entrega
 * os rótulos prontos; a pill decide o desenho.
 *
 * Dois regimes, pela régua `wide` do chrome do shell (880px):
 * - LARGO: a fileira de segmentos. Cada segmento é um botão que devolve o alvo
 *   dele (`onExpand`) para o host reabrir a edição no campo certo; a lupa ao
 *   fim re-busca o MESMO pedido.
 * - ESTREITO: a cápsula de duas linhas (`compact`), um botão só — abaixo de
 *   880px cada segmento virava um toco de duas letras. Sem lupa: não cabe.
 * Sem `matchMedia` (WebView antiga, jsdom) o desenho é o LARGO, o "não sei"
 * das superfícies determinísticas.
 *
 * A fileira larga é feita das peças do DS, na régua do protótipo
 * (`resumo_da_busca.tsx`, #4466): o segmento é a PÍLULA DE CAMPO das modais
 * (`fieldPillClass`, 36px), o divisor é o `Separator` de 16px e a lupa é o
 * `Button` `icon-lg` aberto em estádio 36×52 — tudo na mesma altura. Os tons
 * são os do DS nos dois hosts; o shell não re-pinta a fileira.
 *
 * Contratos que os hosts usam:
 * - `data-slot="summary-pill"` na fileira e `data-seg` em cada segmento: é por
 *   eles que o planner do voo devolve o foco ao recolher.
 * - `hidden` (visibility, sem desmontar, `data-hidden`): a pill some enquanto o
 *   planner está expandido, mas o nó fica para receber o foco de volta.
 * - divisor entre segmentos como IRMÃO (não `::before`): o segmento é botão com
 *   halo de hover, e um divisor desenhado fora dele não entra no halo do
 *   vizinho. Com o segmento em hover ou foco de teclado, os dois divisores
 *   vizinhos somem (`opacity`, sem mexer na largura da fileira).
 * - o rótulo vive num filho de bloco (#3608): o segmento é flex, e reticências
 *   só nascem num filho com `white-space: nowrap`. Rótulo em texto sem
 *   `ariaLabel` vira `title` do segmento, para o valor inteiro sobreviver ao
 *   corte no hover.
 */

export type SummaryPillTarget =
  | 'trip_type'
  | 'traveler'
  | 'origin'
  | 'destination'
  | 'route'
  | 'dates'
  | 'edit';

export type SummaryPillSegment = {
  key: Exclude<SummaryPillTarget, 'edit'>;
  /** Chave React quando o mesmo `key` se REPETE na fileira. Ausente, o `key`
   *  serve — cada alvo aparece uma vez. */
  id?: string;
  /** Glifo à esquerda do texto (avião, calendário, avatar). Host fornece. */
  icon?: ReactNode;
  label: ReactNode;
  /** Segunda linha, em tom de apoio, sob o rótulo: o que ele não diz (no multi
   *  do voo, "3 trechos · 10–18 out" sob a cadeia de praças). A altura da barra
   *  não muda. */
  caption?: string;
  /** Nome acessível quando o rótulo visível é mais curto que o que ele significa
   *  (a rota "GRU → REC", que guarda as cidades para a TA e o `title` do hover).
   *  No botão vira `aria-label`; na leitura vira texto só de leitor de tela.
   *  Ausente, o nome é o texto do rótulo. */
  ariaLabel?: string;
  /** O rótulo em peso — o que a busca É (a rota). */
  strong?: boolean;
  /** Não encolhe quando a barra aperta (#3608), para o corte cair no vizinho
   *  elástico: a janela da locação, que sem o fim perde a informação toda,
   *  enquanto o lugar ainda se lê pelo começo. */
  firm?: boolean;
};

export type SummaryPillProps = {
  segments: readonly SummaryPillSegment[];
  /** As duas linhas do regime estreito: o pedido em cima, o contexto embaixo.
   *  `when` fecha a linha de cima e não encolhe: com um lugar longo, é ele que
   *  ganha reticências, e as datas continuam inteiras (#4513, L1). */
  compact: { primary: string; when?: string; secondary: string };
  /** O nome do grupo, o MESMO nos dois regimes: o leitor de tela acha o
   *  "contexto da busca" no mesmo lugar. */
  ariaLabel: string;
  /** Planner expandido: some SEM desmontar. */
  hidden?: boolean;
  /** Pedido de outro (o carro do agente): sem botões, sem lupa. */
  readOnly?: boolean;
  /** Clique num segmento (o alvo dele) ou na cápsula estreita (`route`, o
   *  pedido inteiro). Ignorado em `readOnly`. */
  onExpand?: (target: SummaryPillTarget) => void;
  /** A lupa e o nome dela, pareados: um sem o outro nasceria sem nome
   *  acessível. Ausente ⇒ sem botão. */
  search?: { onSearch: () => void; label: string };
};

const NARROW_QUERY = below('wide');

/** O `title` do segmento: o nome longo quando o host deu um, senão o próprio
 *  rótulo quando é texto (um rótulo composto não tem versão em texto plano). */
function segmentTitle(seg: SummaryPillSegment): string | undefined {
  return seg.ariaLabel ?? (typeof seg.label === 'string' ? seg.label : undefined);
}

/** A pílula de campo das modais, sem casca (`ghost`): a fileira não tem borda
 *  própria. `strong` é a rota em peso; `firm` não encolhe (#3608). */
function segmentClass(seg: SummaryPillSegment, interactive: boolean): string {
  return [
    fieldPillClass({ variant: 'ghost', interactive }),
    seg.strong ? 'font-semibold' : '',
    seg.firm ? 'shrink-0' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

const LABEL_CLASS = 'block min-w-0 truncate';

function SegmentLabel({ seg }: Readonly<{ seg: SummaryPillSegment }>): ReactNode {
  if (seg.caption === undefined) return seg.label;
  // Duas linhas (#3239 C4) cabem nos 36px do segmento: 14 + 12px a 1.15.
  return (
    <span className="inline-flex flex-col items-start leading-[1.15]">
      {seg.label}
      <span className="text-muted-foreground text-xs font-normal">{seg.caption}</span>
    </span>
  );
}

function Glyph({ icon }: Readonly<{ icon: ReactNode }>): ReactNode {
  // O tom do glifo da pílula de campo, sem o `size-3.5` dela: o glifo aqui é
  // do host e pode ser o avatar de 20px do viajante, não só um ícone de 14px.
  // Não encolhe: com o rótulo elástico ao lado (#3608), o flex espremeria o
  // glifo a um ponto antes de a reticência entrar.
  return (
    <span className="inline-flex shrink-0 text-(--sand-500)" aria-hidden="true">
      {icon}
    </span>
  );
}

/** O divisor some quando o segmento VIZINHO, de qualquer lado, está em hover ou
 *  foco de teclado (#4466): o halo do segmento não fica cortado por um traço.
 *  Só botão acende — a variante de leitura não tem hover. `opacity`, e não
 *  `display`, para a fileira não mudar de largura. */
const DIVIDER_CLASS =
  'data-vertical:h-4 data-vertical:self-center transition-opacity duration-150 ' +
  '[button:hover+&]:opacity-0 [button:focus-visible+&]:opacity-0 ' +
  '[&:has(+button:hover)]:opacity-0 [&:has(+button:focus-visible)]:opacity-0';

function ReadOnlySegment({ seg }: Readonly<{ seg: SummaryPillSegment }>): ReactNode {
  const title = segmentTitle(seg);
  // `<span>` genérico não ganha nome por `aria-label`: o nome completo entra
  // como TEXTO só de leitor de tela e o rótulo visível sai da árvore.
  return (
    <span className={segmentClass(seg, false)} {...(title ? { title } : {})}>
      <span className={LABEL_CLASS} {...(seg.ariaLabel ? { 'aria-hidden': 'true' } : {})}>
        {seg.icon && <Glyph icon={seg.icon} />}
        <SegmentLabel seg={seg} />
      </span>
      {seg.ariaLabel && <span className="sr-only">{seg.ariaLabel}</span>}
    </span>
  );
}

function SegmentButton({
  seg,
  onExpand,
}: Readonly<{ seg: SummaryPillSegment; onExpand: (target: SummaryPillTarget) => void }>) {
  const title = segmentTitle(seg);
  return (
    <button
      type="button"
      className={segmentClass(seg, true)}
      data-seg={seg.key}
      {...(seg.ariaLabel ? { 'aria-label': seg.ariaLabel } : {})}
      {...(title ? { title } : {})}
      onClick={() => {
        onExpand(seg.key);
      }}
    >
      {seg.icon && <Glyph icon={seg.icon} />}
      <span className={LABEL_CLASS}>
        <SegmentLabel seg={seg} />
      </span>
    </button>
  );
}

/** A cápsula de duas linhas do regime estreito. Utilitárias do DS: monta sob
 *  uma raiz `genui2`. `pointer-events-auto` porque a `.tv-top` do shell é
 *  `pointer-events: none` (o clique passa no vazio da barra) e cada controle
 *  re-habilita o próprio.
 *
 *  A régua é a do Figma (nó 6286:88861, #6186): a cápsula PREENCHE a coluna
 *  central da barra, entre a volta e a mala, e as linhas são `text-title`
 *  (15/22, semibold) e `text-detail` (13/18). O desenho tem ~60px de altura
 *  numa barra mais alta; aqui ela enche a primeira linha da `ScreenBar` (56px,
 *  `min-h-14`) — crescer a barra mexe na reserva do topo do shell inteiro. */
function CompactSummary({
  primary,
  when,
  secondary,
  ariaLabel,
  hidden,
  onExpand,
}: Readonly<{
  primary: string;
  when?: string;
  secondary: string;
  ariaLabel: string;
  hidden: boolean;
  onExpand: (() => void) | undefined;
}>): ReactNode {
  const capsule =
    'border-border bg-card shadow-md flex min-h-14 w-full min-w-0 max-w-full flex-col items-center justify-center rounded-full border px-5 py-1 text-center';
  const lines = (
    <>
      <span className="text-foreground text-title flex max-w-full whitespace-nowrap">
        {primary && <span className="min-w-0 truncate">{primary}</span>}
        {when && (
          <span className="shrink-0 whitespace-pre">
            {primary ? ' · ' : ''}
            {when}
          </span>
        )}
      </span>
      <span className="text-muted-foreground text-detail max-w-full truncate">{secondary}</span>
    </>
  );
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`pointer-events-auto flex w-full min-w-0 max-w-full justify-center${hidden ? ' invisible' : ''}`}
      {...(hidden ? { 'data-hidden': 'true' } : {})}
    >
      {onExpand ? (
        <button type="button" className={capsule} onClick={onExpand}>
          {lines}
        </button>
      ) : (
        <div className={capsule}>{lines}</div>
      )}
    </div>
  );
}

export function SummaryPill({
  segments,
  compact,
  ariaLabel,
  hidden = false,
  readOnly = false,
  onExpand,
  search,
}: Readonly<SummaryPillProps>): ReactNode {
  const narrow = useMediaQuery(NARROW_QUERY);
  const expand = readOnly ? undefined : onExpand;
  if (narrow) {
    return (
      <CompactSummary
        {...compact}
        ariaLabel={ariaLabel}
        hidden={hidden}
        onExpand={
          expand &&
          (() => {
            expand('route');
          })
        }
      />
    );
  }
  return (
    /* O embrulho toma a largura que o host dá (contenção de inline-size), e a
       fileira dentro dele é dimensionada pelo conteúdo: os segmentos encolhem
       até o slot em vez de empurrá-lo. Só botões moram aqui dentro — nenhum
       popover —, então a contenção não vira bloco-contêiner de nada
       posicionado. `pointer-events-auto` pelo mesmo motivo da cápsula estreita
       (a `.tv-top` do shell é `pointer-events: none`). */
    <div className="flex min-w-0 flex-auto justify-center [container-type:inline-size]">
      <div
        className={`text-foreground pointer-events-auto mx-auto inline-flex min-w-0 items-center gap-0.5 px-2 py-1.5 text-sm font-medium whitespace-nowrap${hidden ? ' invisible' : ''}`}
        role="group"
        aria-label={ariaLabel}
        data-slot="summary-pill"
        {...(hidden ? { 'data-hidden': 'true' } : {})}
      >
        {segments.map((seg, i) => (
          <Fragment key={seg.id ?? seg.key}>
            {i > 0 && <Separator orientation="vertical" aria-hidden className={DIVIDER_CLASS} />}
            {expand ? <SegmentButton seg={seg} onExpand={expand} /> : <ReadOnlySegment seg={seg} />}
          </Fragment>
        ))}
        {expand && search && (
          /* O estádio 36×52 do protótipo: o degrau `icon-lg` dá o quadrado de
             36, e só a largura escrita abre o estádio. COLADO ao fim do resumo,
             não um disco solto na ponta da barra. */
          <Button
            type="button"
            size="icon-lg"
            className="ml-3 h-9 w-13 rounded-full"
            aria-label={search.label}
            onClick={search.onSearch}
          >
            <MagnifyingGlassIcon size={16} aria-hidden />
          </Button>
        )}
      </div>
    </div>
  );
}
